<template>
  <div 
    data-testid="transaction-matching-consolidated-results" 
    :class="rootClasses"
  >
    <!-- Loading State -->
    <div 
      v-if="loading"
      data-testid="transaction-matching-consolidated-results__loading-state"
      class="consolidated-results__loading"
    >
      <img 
        data-testid="transaction-matching-consolidated-results__loading-image"
        src="/img/loader_new.svg" 
        alt="Loading"
        class="consolidated-results__loading-image"
      />
    </div>

    <!-- Data Display -->
    <div v-else>
      <TMResultsDataTableToolbar
        :variant="variant"
        :active-filter-count="activeFilterCount"
        :has-active-filters="hasActiveFilters"
        :active-sort-count="activeSortCount"
        :applied-sort-length="appliedSort.length"
        :has-unsaved-header-changes="hasUnsavedHeaderChanges"
        :total-count="totalCount"
        :page="page"
        :page-count="pageCount"
        :download-history="filteredDownloadHistory"
        :loading-history="loadingHistory"
        :max-export-records="MAX_EXCEL_EXPORT_RECORDS"
        :show-manual-tag="showManualTag"
        :show-sort-modal="showSortModal"
        :active-sort-tab="activeSortTab"
        :available-sort-columns="availableSortColumns"
        :saved-sort="savedSort"
        :playground-sort="playgroundSort"
        :enable-saved-filter-sort="enableSavedFilterSort"
        @open-filter="openFilterModal"
        @clear-filters="clearAllFilters"
        @open-sort="openSortModal"
        @clear-sort="clearAllSort"
        @save-header-order="saveHeaderOrder"
        @page-change="handlePageChange"
        @download-template="downloadTemplate"
        @generate-excel="handleGenerateExcel"
        @refresh-download-history="$emit('refresh-download-history')"
        @download-history-file="$emit('download-history-file', $event)"
        @bulk-manual-match="openBulkManualMatchingDialog"
        @close-sort="closeSortModal"
        @update:active-sort-tab="activeSortTab = $event"
        @toggle-sort-column="toggleSortColumn"
        @set-sort-direction="setSortDirection"
        @reset-sort="resetSort"
        @apply-sort="applySort"
      />

      <TMResultsTableWrapper
        :table-headers="tableHeaders"
        :rows="consolidatedData"
        :variant="variant"
        :applied-sort="appliedSort"
        :applied-filters="appliedFilters"
        :column-type-map="columnTypeMap"
        :draggable-headers="true"
        @drag-start="handleDragStart"
        @drag-over="handleDragOver"
        @drag-leave="handleDragLeave"
        @drop="handleDrop"
        @drag-end="handleDragEnd"
      />

      <!-- Empty State -->
      <div
        v-if="!consolidatedData || consolidatedData.length === 0"
        data-testid="transaction-matching-consolidated-results__empty-state"
        class="consolidated-results__empty"
      >
        <span class="consolidated-results__empty-text">{{ variant === 'v2' ? 'No data available' : 'No consolidated data available' }}</span>
      </div>
    </div>

    <!-- Filter Modal -->
    <div 
      v-if="showFilterModal"
      data-testid="transaction-matching-consolidated-results__filter-modal-overlay"
      class="consolidated-results__filter-modal-overlay"
      @click.self="closeFilterModal"
    >
      <div 
        data-testid="transaction-matching-consolidated-results__filter-modal"
        class="consolidated-results__filter-modal"
      >
        <!-- Modal Header -->
        <div class="consolidated-results__filter-modal-header">
          <h3 class="consolidated-results__filter-modal-title">Filter Consolidated Data</h3>
          <button
            data-testid="transaction-matching-consolidated-results__filter-modal-close"
            type="button"
            class="consolidated-results__filter-modal-close"
            @click="closeFilterModal"
          >
            <svg class="consolidated-results__filter-modal-close-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Tab Navigation -->
        <div v-if="enableSavedFilterSort" class="consolidated-results__tabs">
          <button
            data-testid="transaction-matching-consolidated-results__filter-tab-saved"
            :class="[
              'consolidated-results__tab',
              activeFilterTab === 'saved' ? 'consolidated-results__tab--active' : ''
            ]"
            @click="activeFilterTab = 'saved'"
          >
            Saved Filters
          </button>
          <button
            data-testid="transaction-matching-consolidated-results__filter-tab-playground"
            :class="[
              'consolidated-results__tab',
              activeFilterTab === 'playground' ? 'consolidated-results__tab--active' : ''
            ]"
            @click="activeFilterTab = 'playground'"
          >
            Filter
          </button>
        </div>

        <!-- Modal Body: Filter Builder -->
        <div class="consolidated-results__filter-modal-body">
          <transition name="tab-slide" mode="out-in">
            <TMResultsFilterBuilder
              :key="effectiveFilterTab"
              test-id-prefix="transaction-matching-consolidated-results"
              :filter-groups="getCurrentFilterGroups()"
              :grouping-connector="getCurrentGroupingConnector()"
              :filter-columns="filterColumns"
              :filter-operators="filterOperators"
              :has-active-filters="hasActiveFilters"
              @set-grouping-connector="setCurrentGroupingConnector"
              @add-group="addGroup"
              @remove-group="removeGroup"
              @reset="resetFilters"
              @apply="applyFilters"
              @update-group-name="updateGroupName"
              @update-group-connector="updateGroupConnector"
              @update-condition="updateCondition"
              @add-condition="addCondition"
              @remove-condition="removeCondition"
            />
          </transition>
        </div>
      </div>
    </div>

    <!-- Bulk Manual Matching Dialog -->
    <v-dialog
      v-model="showBulkMatchDialog"
      data-testid="transaction-matching-consolidated-results__bulk-match-dialog"
      :max-width="bulkMatchErrors.length > 0 ? '800px' : '500px'"
      persistent
    >
      <v-card data-testid="transaction-matching-consolidated-results__bulk-match-card">
        <v-card-title data-testid="transaction-matching-consolidated-results__bulk-match-dialog-title">
          <span class="text-h6">{{ variant === 'v2' ? 'Bulk Manual Tag' : 'Bulk Manual Matching - Consolidated' }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            data-testid="transaction-matching-consolidated-results__bulk-match-dialog-close"
            :disabled="bulkMatchProcessing"
            @click="closeBulkMatchDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <!-- Processing Loader -->
        <v-progress-linear
          v-if="bulkMatchProcessing"
          data-testid="transaction-matching-consolidated-results__bulk-match-loader"
          indeterminate
          color="primary"
          height="3"
          class="mb-3"
        ></v-progress-linear>

        <v-card-text>
          <!-- Error State: Show validation errors table -->
          <div 
            v-if="bulkMatchErrors.length > 0 || bulkMatchErrorMessage"
            data-testid="transaction-matching-consolidated-results__bulk-match-error-section"
            class="bulk-match-error-section"
          >
            <!-- Overall Error Message -->
            <v-alert
              data-testid="transaction-matching-consolidated-results__bulk-match-error-alert"
              type="error"
              dense
              class="mb-3"
            >
              {{ bulkMatchErrorMessage || 'Validation failed. Please review the errors below.' }}
            </v-alert>

            <!-- Row-level Errors Table -->
            <div 
              v-if="bulkMatchErrors.length > 0"
              data-testid="transaction-matching-consolidated-results__bulk-match-error-table-wrapper"
              class="bulk-match-error-table-wrapper"
            >
              <table 
                data-testid="transaction-matching-consolidated-results__bulk-match-error-table"
                class="bulk-match-error-table"
              >
                <thead>
                  <tr>
                    <th data-testid="transaction-matching-consolidated-results__bulk-match-error-header-sheet">Sheet</th>
                    <th data-testid="transaction-matching-consolidated-results__bulk-match-error-header-source">Source</th>
                    <th data-testid="transaction-matching-consolidated-results__bulk-match-error-header-table1sno">Table1 SNo</th>
                    <th data-testid="transaction-matching-consolidated-results__bulk-match-error-header-table2sno">Table2 SNo</th>
                    <th data-testid="transaction-matching-consolidated-results__bulk-match-error-header-error">Error</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="(error, index) in bulkMatchErrors" 
                    :key="index"
                    data-testid="transaction-matching-consolidated-results__bulk-match-error-row"
                  >
                    <td data-testid="transaction-matching-consolidated-results__bulk-match-error-cell-sheet">{{ error.sheet || '-' }}</td>
                    <td data-testid="transaction-matching-consolidated-results__bulk-match-error-cell-source">{{ error.source || '-' }}</td>
                    <td data-testid="transaction-matching-consolidated-results__bulk-match-error-cell-table1sno">{{ error.table1Sno || '-' }}</td>
                    <td data-testid="transaction-matching-consolidated-results__bulk-match-error-cell-table2sno">{{ error.table2Sno || '-' }}</td>
                    <td data-testid="transaction-matching-consolidated-results__bulk-match-error-cell-error">{{ error.error || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Retry Button -->
            <div class="mt-3 d-flex justify-end">
              <v-btn
                data-testid="transaction-matching-consolidated-results__bulk-match-retry-btn"
                color="primary"
                outlined
                @click="resetBulkMatchErrors"
              >
                <v-icon left small>mdi-refresh</v-icon>
                <span>Try Again</span>
              </v-btn>
            </div>
          </div>

          <!-- Upload State: Show file upload component -->
          <div 
            v-else
            data-testid="transaction-matching-consolidated-results__bulk-match-upload-section"
          >
            <DragDropfile
              data-testid="transaction-matching-consolidated-results__bulk-match-upload"
              hidedetails
              :files="bulkMatchFiles"
              :disabled="bulkMatchProcessing"
              @update:files="handleBulkMatchFilesUpload"
              :resetTrigger="bulkMatchResetFlag"
            />
          </div>
        </v-card-text>

        <!-- Actions: Only show when not in error state -->
        <v-card-actions v-if="bulkMatchErrors.length === 0 && !bulkMatchErrorMessage">
          <v-spacer></v-spacer>
          <v-btn
            data-testid="transaction-matching-consolidated-results__bulk-match-submit"
            color="primary"
            :loading="bulkMatchProcessing"
            :disabled="bulkMatchProcessing || bulkMatchFiles.length === 0"
            @click="submitBulkManualMatching"
          >
            <span>Submit</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/**
 * TMResultsDataTable Component
 * 
 * Generic reusable data table for displaying annexure collection data with
 * advanced filtering, sorting, pagination, column reorder, and bulk matching.
 * Used by both V1 consolidated results and V2 per-tab results.
 * 
 * @component
 */

import DragDropfile from "../../../../../../views/Inputs/DragDropfile.vue";
import TMResultsDataTableToolbar from './TMResultsDataTableToolbar.vue';
import TMResultsFilterBuilder from './TMResultsFilterBuilder.vue';
import TMResultsTableWrapper from './TMResultsTableWrapper.vue';
import { mapActions, mapGetters } from 'vuex';
import {
  // Constants
  FILTER_OPERATORS,
  DEFAULT_PAGE_LIMIT,
  MAX_EXCEL_EXPORT_RECORDS,
  TEMPLATE_FILTER_CONFIG,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  // Utility functions
  createDefaultCondition,
  createDefaultFilterGroup,
  ensureFilterIds,
  formatHeaderText,
  buildColumnTypeMap,
  operatorRequiresValue,
  buildFilterPayload,
  resolveExportDataFilters,
  validateBulkMatchFiles,
  getConsolidatedCollection,
  transformBulkMatchErrors
} from './tmResultsDataTableUtils.js';

export default {
  name: 'TMResultsDataTable',

  components: {
    DragDropfile,
    TMResultsDataTableToolbar,
    TMResultsFilterBuilder,
    TMResultsTableWrapper
  },

  props: {
    /**
     * Analysis details object containing bpid, _id, etc.
     */
    analysisDetails: {
      type: Object,
      required: true,
      default: () => ({})
    },

    /**
     * Direct collection name to load data from.
     * When provided, takes priority over annexureList lookup.
     * Used by V2 results where each tab passes its collection directly.
     */
    collection: {
      type: String,
      default: ''
    },

    /**
     * Annexure list from run details (V1 fallback).
     * Used to find 'Consolidated Annexure' collection when collection prop is not provided.
     */
    annexureList: {
      type: Array,
      default: () => []
    },

    /**
     * Verification ID
     */
    verificationId: {
      type: String,
      default: ''
    },

    runDetails:{
      type: Object,
      default: () => ({})
    },
    /**
     * File configuration info containing column types and mappings
     * Structure: setup.fileConfig[id].columnmap — type on each entry (e.g. date1, string1)
     */
    fileConfigInfo: {
      type: Object,
      default: () => ({})
    },

    /**
     * Component variant type.
     * 'v1' — used from TransactionMatching (legacy)
     * 'v2' — used from TransactionMatchingV2
     * @values v1, v2
     */
    variant: {
      type: String,
      default: 'v1',
      validator: (value) => ['v1', 'v2'].includes(value)
    },

    /**
     * Array of download history files for DownloadExcel dropdown (V2)
     */
    downloadHistory: {
      type: Array,
      default: () => []
    },

    /**
     * Loading state for download history (V2)
     */
    loadingHistory: {
      type: Boolean,
      default: false
    },

    /**
     * Original annexure tab name (e.g. "Table 1", "Report").
     * Used by V2 to send the tab name in the download API payload.
     */
    tabName: {
      type: String,
      default: ''
    },

    /**
     * File configuration ID for this tab (V2 only).
     * Resolved from fileConfigInfo by matching against fileList.
     */
    fileConfigId: {
      type: String,
      default: ''
    },

    /**
     * When false, hides Saved Filters / Saved Sort tabs and uses plain Filter / Sort only.
     */
    enableSavedFilterSort: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      // Constants exposed to template
      MAX_EXCEL_EXPORT_RECORDS,

      // Loading state
      loading: false,
      
      // Table data
      consolidatedData: [],
      tableHeaders: [],
      
      // Pagination state
      page: 1,
      limit: DEFAULT_PAGE_LIMIT,
      totalCount: 0,
      pageCount: 0,
      
      // === HEADERS ===
      apiHeaders: [],                 // Raw headers array from API response - used for filter/sort dropdowns
      savedHeaderOrder: [],           // Persisted order from API (headers in response)
      currentHeaderOrder: [],         // Active header order in UI (can be reordered)
      draggedHeaderIndex: null,       // For drag & drop tracking
      draggedHeaderKey: null,          // Key of the dragged header
      currentDropTarget: null,         // Current drop target element
      hasUnsavedHeaderChanges: false, // Track if headers were reordered but not saved
      
      // === FILTERS ===
      savedFilters: { groups: [], group_connectors: [] },  // Persisted from API (defaultFilters)
      playgroundFilters: { groups: [], group_connectors: [] }, // User's temporary edits (persisted across modal open/close)
      modalFilterSnapshot: null,
      appliedFilters: { groups: [], group_connectors: [] },    // Currently applied to data
      activeFilterTab: 'saved',       // 'saved' | 'playground' - defaults to 'saved'
      playgroundFilterGroupingConnector: 'OR',  // Connector for playground filters
      
      // === SORT ===
      savedSort: [],                  // Persisted from API (defaultSort)
      playgroundSort: [],             // User's temporary edits (persisted across dropdown open/close)
      appliedSort: [],                // Currently applied to data (replaces: sortItems)
      activeSortTab: 'saved',         // 'saved' | 'playground' - defaults to 'saved'
      
      // === INITIAL LOAD FLAG ===
      initialLoadComplete: false,     // Tracks if initial load with saved defaults has been done
      
      // Filter modal state
      showFilterModal: false,
      
      // Sort modal state
      showSortModal: false,
      
      // Filter configuration
      filterOperators: FILTER_OPERATORS,
      filterColumns: [],
      
      // Bulk Manual Matching state
      showBulkMatchDialog: false,
      bulkMatchFiles: [],
      bulkMatchResetFlag: false,
      bulkMatchProcessing: false,
      bulkMatchErrors: [],
      bulkMatchErrorMessage: ''
    };
  },

  computed: {
    ...mapGetters('transactionMatching', ['lastActiveFilterTab', 'formData']),

    /**
     * Last filter tab used when filters were applied for this collection.
     * @returns {'saved'|'playground'|null}
     */
    lastAppliedFilterTab() {
      return this.lastActiveFilterTab(this.consolidatedCollection);
    },

    /**
     * Root element CSS classes based on variant prop
     * @returns {Object} Class map
     */
    rootClasses() {
      return {
        'transaction-matching-consolidated-results': true,
        'transaction-matching-consolidated-results--v1': this.variant === 'v1',
        'transaction-matching-consolidated-results--v2': this.variant === 'v2'
      };
    },

    /**
     * Column types from fileConfig for this tab (scoped by fileConfigId when set).
     * @returns {Object} Map of column name -> type (string1, date1, amount1, etc.)
     */
    columnTypeMap() {
      return buildColumnTypeMap(this.fileConfigInfo, this.fileConfigId);
    },

    /**
     * Get the active collection name.
     * Prefers the direct `collection` prop (V2 usage).
     * Falls back to searching annexureList for 'Consolidated Annexure' (V1 usage).
     * @returns {string} Collection name
     */
    consolidatedCollection() {
      return this.collection || getConsolidatedCollection(this.annexureList);
    },

    /**
     * For V2, filter download history to only show entries matching the current tab.
     * Matches against the `tables` array on each download entry.
     * V1 passes the full list unfiltered.
     * @returns {Array} Filtered download history
     */
    filteredDownloadHistory() {
      if (this.variant !== 'v2' || !this.tabName) return this.downloadHistory;
      return this.downloadHistory.filter(
        file => !Array.isArray(file.tables) || file.tables.length === 0 || file.tables.includes(this.tabName)
      );
    },

    /**
     * Hide bulk manual tag when the current tab matches hideManualTagTabs from TM form-data.
     * @returns {boolean}
     */
    showManualTag() {
      const tab = String(this.tabName || '').toLowerCase().replace(/s$/, '');
      const hiddenTabs = this.formData?.hideManualTagTabs || [];
      return !hiddenTabs.some((item) => String(item || '').toLowerCase().replace(/s$/, '') === tab);
    },

    /**
     * Active filter tab, forced to playground when saved tabs are disabled.
     */
    effectiveFilterTab() {
      return this.enableSavedFilterSort ? this.activeFilterTab : 'playground';
    },

    /**
     * Active sort tab, forced to playground when saved tabs are disabled.
     */
    effectiveSortTab() {
      return this.enableSavedFilterSort ? this.activeSortTab : 'playground';
    },

    /**
     * Check if there are active filters applied
     * @returns {boolean} True if filters are active
     */
    hasActiveFilters() {
      return this.appliedFilters?.groups?.length > 0;
    },

    /**
     * Count of active filter conditions (only valid/filled conditions)
     * A condition is valid if it has a value (for operators that require value) or is a null operator.
     * Priority: Prefer the filters currently applied to the table, then show playground edits, and finally fall back to saved defaults.
     * @returns {number} Total number of valid filter conditions
     */
    activeFilterCount() {
      // Helper function to check if a condition is valid (has value or is null operator)
      const isValidCondition = (condition) => {
        if (!condition || !condition.column) return false;
        // Null operators don't require a value
        if (!operatorRequiresValue(condition.operator)) return true;
        // For other operators, value must be non-empty
        // Handle both string and numeric values (API may return numbers)
        if (condition.value === null || condition.value === undefined) return false;
        if (typeof condition.value === 'number') return !isNaN(condition.value);
        return String(condition.value).trim() !== '';
      };

      // Helper function to count valid conditions in filter groups
      const countValidConditions = (filters) => {
        if (!filters || !filters.groups) return 0;
        return filters.groups.reduce((count, group) => {
          const validConditions = (group.conditions || []).filter(isValidCondition);
          return count + validConditions.length;
        }, 0);
      };

      // Always show the count of filters currently impacting the dataset first
      const appliedCount = countValidConditions(this.appliedFilters);
      if (appliedCount > 0) {
        return appliedCount;
      }

      // If user is crafting new playground filters, surface that count next
      const playgroundCount = countValidConditions(this.playgroundFilters);
      if (playgroundCount > 0) {
        return playgroundCount;
      }

      // Fall back to saved defaults when no applied or playground filters are active
      return countValidConditions(this.savedFilters);
    },

    /**
     * Count of active sort columns
     * Priority: If playground sort has content, show playground count. Otherwise show saved sort count.
     * @returns {number} Total number of sort columns
     */
    activeSortCount() {
      // Always show the count of sort currently impacting the dataset first
      if (this.appliedSort && this.appliedSort.length > 0) {
        return this.appliedSort.length;
      }

      // If user is crafting new playground sort, surface that count next
      if (this.playgroundSort && this.playgroundSort.length > 0) {
        return this.playgroundSort.length;
      }

      // Fall back to saved defaults
      return this.savedSort?.length || 0;
    },

    /**
     * Get available columns for sorting (from API headers, not derived from data)
     * @returns {Array} Array of sortable columns
     */
    availableSortColumns() {
      // Use apiHeaders to ensure dropdowns work even with 0 records
      return this.apiHeaders
        .filter(header => !header.startsWith('_'))  // Exclude internal fields like _id
        .map(header => ({
          key: header,
          label: this.formatHeaderText(header)
        }));
    }
  },

  watch: {
    /**
     * Watch for changes in the direct collection prop (V2 usage)
     * Reload data when collection changes
     */
    collection(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.resetComponentState();
        this.initialize();
      }
    },

    /**
     * Watch for changes in annexureList (V1 usage)
     * Reload data when annexureList becomes available or changes
     */
    annexureList: {
      handler(newVal, oldVal) {
        // Skip if using direct collection prop
        if (this.collection) return;
        // Only reload if we didn't have data before or collection changed
        const oldCollection = oldVal && Array.isArray(oldVal) 
          ? oldVal.find(item => item && item.name === 'Consolidated Annexure')?.collection 
          : null;
        const newCollection = this.consolidatedCollection;
        
        if (newCollection && oldCollection !== newCollection) {
          this.initialize();
        }
      },
      deep: true
    }
  },

  /**
   * Component is mounted when tab becomes active
   * Load fresh data from API
   */
  mounted() {
    this.initialize();
  },

  /**
   * Component is destroyed when tab becomes inactive
   * Clean up event listeners and reset state
   */
  beforeDestroy() {
    // Reset component state to prevent stale data on re-entry
    this.resetComponentState();
  },

  methods: {
    ...mapActions('transactionMatching', ['fetchAnnexureData', 'fetchFormData']),

    /**
     * Initialize component - load consolidated data
     */
    async initialize() {
      try {
        await this.fetchFormData();
      } catch (err) {
        console.error('[TMResultsDataTable]: prefetch formData error', err);
      }

      if (this.consolidatedCollection) {
        await this.loadData();
      } else {
        console.warn('[TMResultsDataTable]: No consolidated collection found. Waiting for annexureList.');
      }
    },

    /**
     * Reset all component state to defaults
     */
    resetComponentState() {
      this.consolidatedData = [];
      this.tableHeaders = [];
      this.page = 1;
      this.totalCount = 0;
      this.pageCount = 0;
      
      // Reset header state
      this.apiHeaders = [];
      this.savedHeaderOrder = [];
      this.currentHeaderOrder = [];
      this.hasUnsavedHeaderChanges = false;
      
      // Reset filter state
      this.savedFilters = { groups: [], group_connectors: [] };
      this.playgroundFilters = { groups: [], group_connectors: [] };
      this.appliedFilters = { groups: [], group_connectors: [] };
      this.activeFilterTab = this.enableSavedFilterSort ? 'saved' : 'playground';
      this.playgroundFilterGroupingConnector = 'OR';
      
      // Reset sort state
      this.savedSort = [];
      this.playgroundSort = [];
      this.appliedSort = [];
      this.activeSortTab = this.enableSavedFilterSort ? 'saved' : 'playground';
      
      // Reset initial load flag
      this.initialLoadComplete = false;

    },

    /**
     * Load consolidated data from API
     */
    async loadData() {
      const collection = this.consolidatedCollection;
      
      if (!collection) {
        console.error('[TMResultsDataTable]: No collection provided');
        return;
      }

      console.log('[TMResultsDataTable]: Loading consolidated data', { 
        collection, 
        page: this.page, 
        limit: this.limit,
        hasFilters: this.appliedFilters?.groups?.length > 0,
        hasSort: this.appliedSort?.length > 0
      });

      this.loading = true;

      try {
        const requestPayload = {
          page: collection,  // Include page identifier for fetching defaults
          filters: this.appliedFilters,
          pagination: {
            page: this.page,
            limit: this.limit
          }
        };

        if (this.variant === 'v2') {
          requestPayload.version = 'v2';
          if (this.fileConfigId) {
            requestPayload.fileConfig = this.fileConfigId;
          }
        }

        // Add sorting if present
        if (this.appliedSort.length > 0) {
          requestPayload.sort = this.appliedSort.map(item => ({
            field: item.key,
            direction: item.direction
          }));
        }

        const data = await this.fetchAnnexureData({
          collection,
          ...requestPayload
        });

        // Data records
        this.consolidatedData = data.records || [];

        // Pagination
        this.totalCount = data.totalCount || 0;
        this.page = data.page || 1;
        this.pageCount = data.totalPages || Math.ceil(this.totalCount / this.limit);

        // === Store headers from API (always use this for dropdowns) ===
        this.apiHeaders = data.headers || [];

        // === Store defaults from API ===
        // Use ensureFilterIds to add unique IDs to conditions that may be missing them
        // This fixes reactivity issues in the filter builder where conditions without IDs
        // would cause operator changes in one condition to reflect in another
        // ensureFilterIds returns null for empty/invalid filters, so we fall back to empty group
        const actualSavedFilters = ensureFilterIds(data.defaultFilters);
        this.savedFilters = actualSavedFilters || this.createEmptyFilterGroup();
        // Ensure defaultSort is a valid array, handle empty object/null cases
        this.savedSort = Array.isArray(data.defaultSort) ? data.defaultSort : [];
        this.savedHeaderOrder = data.headers || [];  // Headers = persisted order

        // Initialize current header order from saved (first load only)
        if (this.currentHeaderOrder.length === 0 && this.savedHeaderOrder.length > 0) {
          this.currentHeaderOrder = [...this.savedHeaderOrder];
        }

        // === Apply saved sort/filters on initial load ===
        // This ensures saved configurations are active when reopening the analysis
        if (!this.initialLoadComplete) {
          this.initialLoadComplete = true;

          let needsReload = false;

          // Apply saved sort if present and not already applied
          if (this.savedSort.length > 0 && this.appliedSort.length === 0) {
            this.appliedSort = JSON.parse(JSON.stringify(this.savedSort));
            needsReload = true;
            console.log('[TMResultsDataTable]: Applied saved sort on initial load', this.appliedSort);
          }

          // Apply saved filters if present and not already applied
          // Only apply if API returned actual saved filters (not just fallback empty group)
          if (actualSavedFilters && this.appliedFilters?.groups?.length === 0) {
            this.appliedFilters = JSON.parse(JSON.stringify(this.savedFilters));
            needsReload = true;
            console.log('[TMResultsDataTable]: Applied saved filters on initial load', this.appliedFilters);
          }

          // Reload with applied defaults to get properly sorted/filtered data
          if (needsReload) {
            console.log('[TMResultsDataTable]: Reloading with saved defaults applied');
            // Continue with current response processing, then reload
            // We need to complete this iteration first to avoid issues
            this.$nextTick(() => {
              this.loadData();
            });
          }
        }

        // Build table headers from currentHeaderOrder (or apiHeaders if not set)
        this.buildTableHeadersFromOrder();

        // Update filter columns from apiHeaders (not from data records!)
        this.updateFilterColumnsFromApiHeaders();

        console.log('[TMResultsDataTable]: Data loaded', {
          count: this.consolidatedData.length,
          total: this.totalCount,
          page: this.page,
          pageCount: this.pageCount,
          apiHeaders: this.apiHeaders.length,
          savedFilters: this.savedFilters?.groups?.length || 0,
          savedSort: this.savedSort?.length || 0
        });
      } catch (err) {
        console.error('[TMResultsDataTable]: Error loading data', err);
        this.$store.commit('sbError', err?.response?.data?.message || err?.message || 'Failed to load consolidated data');
        this.consolidatedData = [];
        this.totalCount = 0;
        this.pageCount = 0;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Build table headers from currentHeaderOrder or apiHeaders
     * Determines numeric columns based on columnTypeMap from fileConfigInfo
     */
    buildTableHeadersFromOrder() {
      const headerOrder = this.currentHeaderOrder.length > 0 
        ? this.currentHeaderOrder 
        : this.apiHeaders;
      
      this.tableHeaders = headerOrder
        .filter(headerKey => !headerKey.startsWith('_'))  // Exclude internal fields
        .map(headerKey => {
          const columnType = this.columnTypeMap[headerKey] || 'string';
          return {
            key: headerKey,
            label: this.formatHeaderText(headerKey),
            isNumeric: columnType === 'number'
          };
        });
    },

    /**
     * Update filter columns from API headers (not derived from data records)
     * This ensures dropdowns work even when filters return 0 records
     */
    updateFilterColumnsFromApiHeaders() {
      this.filterColumns = this.apiHeaders
        .filter(header => !header.startsWith('_'))  // Exclude internal fields like _id
        .map(header => ({
          label: this.formatHeaderText(header),
          value: header
        }));
    },

    /**
     * Create empty filter group structure
     * @returns {Object} Empty filter group with one condition
     */
    createEmptyFilterGroup() {
      return {
        groups: [createDefaultFilterGroup()],
        group_connectors: []
      };
    },

    /**
     * Save filter, sort, and/or header configuration to API
     * @param {Object} config - Configuration object { filters, sort, headers }
     */
    async saveFilterSortConfig({ filters = null, sort = null, headers = null }) {
      const pid = this.analysisDetails?.pid || this.$route.params.id || '';
      const rawTasks = this.analysisDetails?.linkedtasks;
      const linkedtasks = (Array.isArray(rawTasks) ? rawTasks : [])
        .map((task) => (task && task._id) || task)
        .filter(Boolean);

      const payload = {
        page: this.consolidatedCollection
      };

      // Cycle id for botTmDeny. linkedtasks covers non-manager task access.
      if (pid) payload.pid = pid;
      if (linkedtasks.length) payload.linkedtasks = linkedtasks;
      if (filters !== null) payload.filters = filters;
      if (sort !== null) payload.sort = sort;
      if (headers !== null) payload.headers = headers;

      try {
        const response = await this.axios.post('/v3/bot/analysis/save-filter-sort', payload);
        
        if (response.data.status === 'success') {
          this.$store.commit('sbSuccess', response.data.message || 'Configuration saved successfully');
          
          // Update saved state based on what was saved
          if (filters !== null) this.savedFilters = filters;
          if (sort !== null) this.savedSort = sort;
          if (headers !== null) {
            this.savedHeaderOrder = headers;
            this.hasUnsavedHeaderChanges = false;
          }
        } else {
          throw new Error(response.data.message || 'Failed to save configuration');
        }
      } catch (err) {
        console.error('[TMResultsDataTable]: Error saving configuration', err);
        this.$store.commit('sbError', err.message || 'Failed to save configuration');
        throw err;  // Re-throw so caller knows it failed
      }
    },

    // ========================================================================
    // Drag & Drop Header Methods
    // ========================================================================

    /**
     * Handle drag start on table header
     * @param {string} headerKey - Key of the dragged header
     * @param {number} index - Index of the dragged header
     * @param {DragEvent} event - Drag event
     */
    handleDragStart(headerKey, index, event) {
      this.draggedHeaderKey = headerKey;
      this.draggedHeaderIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', headerKey);
      
      // Add visual feedback
      event.target.classList.add('consolidated-results__header-cell--dragging');
    },

    /**
     * Handle drag over on table header
     * @param {string} headerKey - Key of the target header
     * @param {number} index - Index of the target header
     * @param {DragEvent} event - Drag event
     */
    handleDragOver(headerKey, index, event) {
      if (this.draggedHeaderKey === null) return;
      
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      
      const targetCell = event.currentTarget;
      
      // Only update drop target if it's different from current
      if (headerKey !== this.draggedHeaderKey && this.currentDropTarget !== targetCell) {
        // Remove class from previous drop target
        if (this.currentDropTarget) {
          this.currentDropTarget.classList.remove('consolidated-results__header-cell--drop-target');
        }
        // Add class to new drop target
        targetCell.classList.add('consolidated-results__header-cell--drop-target');
        this.currentDropTarget = targetCell;
      }
    },

    /**
     * Handle drag leave on table header
     * @param {DragEvent} event - Drag event
     */
    handleDragLeave(event) {
      // Only remove if we're leaving the element (not entering a child)
      const relatedTarget = event.relatedTarget;
      if (!event.currentTarget.contains(relatedTarget)) {
        event.currentTarget.classList.remove('consolidated-results__header-cell--drop-target');
        if (this.currentDropTarget === event.currentTarget) {
          this.currentDropTarget = null;
        }
      }
    },

    /**
     * Handle drop on table header
     * @param {string} targetHeaderKey - Key of the target header
     * @param {number} targetIndex - Index where to drop the header
     * @param {DragEvent} event - Drag event
     */
    handleDrop(targetHeaderKey, targetIndex, event) {
      event.preventDefault();
      
      if (this.draggedHeaderKey === null || this.draggedHeaderKey === targetHeaderKey) {
        this.clearDragState();
        return;
      }
      
      // Find actual indices in currentHeaderOrder using keys
      const sourceIndex = this.currentHeaderOrder.indexOf(this.draggedHeaderKey);
      const destIndex = this.currentHeaderOrder.indexOf(targetHeaderKey);
      
      if (sourceIndex === -1 || destIndex === -1) {
        console.warn('[TMResultsDataTable]: Invalid header keys for reorder');
        this.clearDragState();
        return;
      }
      
      // Reorder headers using key-based indices
      const newOrder = [...this.currentHeaderOrder];
      const [draggedItem] = newOrder.splice(sourceIndex, 1);
      newOrder.splice(destIndex, 0, draggedItem);
      
      this.currentHeaderOrder = newOrder;
      this.hasUnsavedHeaderChanges = true;
      
      // Rebuild table headers with new order
      this.buildTableHeadersFromOrder();
      
      console.log('[TMResultsDataTable]: Headers reordered', {
        draggedKey: this.draggedHeaderKey,
        targetKey: targetHeaderKey,
        from: sourceIndex,
        to: destIndex,
        newOrder: this.currentHeaderOrder
      });
      
      this.clearDragState();
    },

    /**
     * Clear drag and drop state
     */
    clearDragState() {
      this.draggedHeaderKey = null;
      this.draggedHeaderIndex = null;
      this.currentDropTarget = null;
      
      // Remove all visual feedback classes
      document.querySelectorAll('.consolidated-results__header-cell--dragging').forEach(el => {
        el.classList.remove('consolidated-results__header-cell--dragging');
      });
      document.querySelectorAll('.consolidated-results__header-cell--drop-target').forEach(el => {
        el.classList.remove('consolidated-results__header-cell--drop-target');
      });
    },

    /**
     * Handle drag end (cleanup)
     */
    handleDragEnd() {
      this.clearDragState();
    },

    /**
     * Save current header order to API
     */
    async saveHeaderOrder() {
      if (!this.hasUnsavedHeaderChanges) return;
      
      console.log('[TMResultsDataTable]: Saving header order', this.currentHeaderOrder);
      
      try {
        await this.saveFilterSortConfig({ headers: this.currentHeaderOrder });
      } catch (err) {
        // Error already handled by saveFilterSortConfig
      }
    },

    /**
     * Handle page change
     * @param {number} newPage - New page number
     */
    handlePageChange(newPage) {
      if (newPage < 1 || newPage > this.pageCount) return;
      
      this.page = newPage;
      this.loadData();
    },

    /**
     * Handle column sort
     * @param {string} key - Column key to sort by
     */
    handleSort(key) {
      const existingIndex = this.appliedSort.findIndex(item => item.key === key);
      
      if (existingIndex === -1) {
        // Add new sort item
        this.appliedSort.push({ key, direction: 'asc' });
      } else {
        const existing = this.appliedSort[existingIndex];
        
        if (existing.direction === 'asc') {
          // Toggle to descending
          this.appliedSort[existingIndex].direction = 'desc';
        } else {
          // Remove sort item
          this.appliedSort.splice(existingIndex, 1);
        }
      }

      // Reset to first page and reload
      this.page = 1;
      this.loadData();
    },

    /**
     * Format header text - replace underscores with spaces
     * @param {string} text - Header text
     * @returns {string} Formatted text
     */
    formatHeaderText(text) {
      return formatHeaderText(text);
    },

    /**
     * Check if operator requires a value input
     * @param {string} operator - Operator value
     * @returns {boolean} True if operator requires value input
     */
    operatorRequiresValue(operator) {
      return operatorRequiresValue(operator);
    },

    // ========================================================================
    // Filter Modal Methods
    // ========================================================================

    /**
     * Open the filter modal
     */
    openFilterModal() {
      // Initialize playground filters from applied filters if empty
      if ((this.playgroundFilters?.groups?.length || 0) === 0 && (this.appliedFilters?.groups?.length || 0) > 0) {
        this.playgroundFilters = JSON.parse(JSON.stringify(this.appliedFilters));
        this.playgroundFilterGroupingConnector = this.appliedFilters.group_connectors?.[0] || 'OR';
      }
      
      // Ensure saved filters has at least one group
      if (!this.savedFilters?.groups || this.savedFilters.groups.length === 0) {
        this.savedFilters = this.createEmptyFilterGroup();
      }
      
      // Ensure playground filters has at least one group
      if (!this.playgroundFilters?.groups || this.playgroundFilters.groups.length === 0) {
        this.playgroundFilters = this.createEmptyFilterGroup();
      }
      
      // Restore last used tab from store, default to saved (or playground when saved tabs disabled)
      this.activeFilterTab = this.enableSavedFilterSort
        ? (this.lastAppliedFilterTab || 'saved')
        : 'playground';
      this.modalFilterSnapshot = {
        playgroundFilters: JSON.parse(JSON.stringify(this.playgroundFilters)),
        playgroundFilterGroupingConnector: this.playgroundFilterGroupingConnector,
        savedFilters: JSON.parse(JSON.stringify(this.savedFilters)),
      };
      this.showFilterModal = true;
    },

    /**
     * Close the filter modal. Restores unsaved edits unless `restore` is false (Apply).
     */
    closeFilterModal(restore = true) {
      this.showFilterModal = false;
      if (restore && this.modalFilterSnapshot) {
        this.playgroundFilters = JSON.parse(JSON.stringify(this.modalFilterSnapshot.playgroundFilters));
        this.playgroundFilterGroupingConnector = this.modalFilterSnapshot.playgroundFilterGroupingConnector;
        this.savedFilters = JSON.parse(JSON.stringify(this.modalFilterSnapshot.savedFilters));
      }
    },

    /**
     * Get current filter groups based on active tab
     * @returns {Array} Current filter groups
     */
    getCurrentFilterGroups() {
      return this.effectiveFilterTab === 'saved' 
        ? this.savedFilters.groups 
        : this.playgroundFilters.groups;
    },

    /**
     * Get current grouping connector based on active tab
     * @returns {string} Current grouping connector
     */
    getCurrentGroupingConnector() {
      if (this.effectiveFilterTab === 'saved') {
        return (this.savedFilters.group_connectors && this.savedFilters.group_connectors[0]) || 'OR';
      } else {
        return this.playgroundFilterGroupingConnector;
      }
    },

    /**
     * Set current grouping connector based on active tab
     * @param {string} connector - Connector value ('AND' or 'OR')
     */
    setCurrentGroupingConnector(connector) {
      if (this.effectiveFilterTab === 'saved') {
        if (!this.savedFilters.group_connectors) {
          this.$set(this.savedFilters, 'group_connectors', []);
        }
        this.$set(this.savedFilters.group_connectors, 0, connector);
      } else {
        this.playgroundFilterGroupingConnector = connector;
      }
    },

    /**
     * Add a new filter group to current tab
     */
    addGroup() {
      const currentGroups = this.getCurrentFilterGroups();
      currentGroups.push(createDefaultFilterGroup());
    },

    /**
     * Remove a filter group from current tab
     * @param {string} groupId - ID of the group to remove
     */
    removeGroup(groupId) {
      const currentGroups = this.getCurrentFilterGroups();
      
      if (currentGroups.length === 1) {
        this.resetFilters();
        return;
      }
      
      const newGroups = currentGroups.filter(g => g.id !== groupId);
      
      if (this.effectiveFilterTab === 'saved') {
        this.savedFilters.groups = newGroups;
      } else {
        this.playgroundFilters.groups = newGroups;
      }
    },

    /**
     * Reset filters to default state for current tab
     */
    resetFilters() {
      const emptyGroup = this.createEmptyFilterGroup();
      
      if (this.effectiveFilterTab === 'saved') {
        this.savedFilters = emptyGroup;
      } else {
        this.playgroundFilters = emptyGroup;
        this.playgroundFilterGroupingConnector = 'OR';
      }
    },

    /**
     * Add a condition to a filter group in current tab
     * @param {string} groupId - ID of the target group
     */
    addCondition(groupId) {
      const currentGroups = this.getCurrentFilterGroups();
      const group = currentGroups.find(g => g.id === groupId);
      if (group) {
        group.conditions.push(createDefaultCondition());
      }
    },

    /**
     * Remove a condition from a filter group in current tab
     * @param {string} groupId - ID of the target group
     * @param {string} conditionId - ID of the condition to remove
     */
    removeCondition(groupId, conditionId) {
      const currentGroups = this.getCurrentFilterGroups();
      const group = currentGroups.find(g => g.id === groupId);
      if (!group) return;

      const remaining = group.conditions.filter(c => c.id !== conditionId);
      
      // Always keep at least one condition
      group.conditions = remaining.length > 0 
        ? remaining 
        : [createDefaultCondition()];
    },

    /**
     * Update a condition field value in current tab
     * @param {string} groupId - ID of the target group
     * @param {string} conditionId - ID of the condition to update
     * @param {string} field - Field name to update
     * @param {*} value - New value
     */
    updateCondition(groupId, conditionId, field, value) {
      const currentGroups = this.getCurrentFilterGroups();
      const group = currentGroups.find(g => g.id === groupId);
      if (!group) return;

      const condition = group.conditions.find(c => c.id === conditionId);
      if (condition) {
        this.$set(condition, field, value);
      }
    },

    /**
     * Update group connector (AND/OR for conditions within group) in current tab
     * @param {string} groupId - ID of the target group
     * @param {string} connector - Connector operator ('AND' or 'OR')
     */
    updateGroupConnector(groupId, connector) {
      const currentGroups = this.getCurrentFilterGroups();
      const group = currentGroups.find(g => g.id === groupId);
      if (group) {
        this.$set(group, 'connector', connector);
      }
    },

    /**
     * Update group name in current tab
     * @param {string} groupId - ID of the target group
     * @param {string} name - New group name
     */
    updateGroupName(groupId, name) {
      const currentGroups = this.getCurrentFilterGroups();
      const group = currentGroups.find(g => g.id === groupId);
      if (group) {
        this.$set(group, 'name', name);
      }
    },

    /**
     * Apply filters based on active tab
     * Uses buildFilterPayload to validate and filter out empty/invalid conditions
     */
    async applyFilters() {
      if (this.effectiveFilterTab === 'saved') {
        // Build validated filter payload (filters out empty conditions)
        const filterPayload = buildFilterPayload(
          this.savedFilters.groups || [],
          this.getCurrentGroupingConnector(),
          this.columnTypeMap
        );
        
        try {
          await this.saveFilterSortConfig({ filters: filterPayload });
          this.appliedFilters = JSON.parse(JSON.stringify(filterPayload));
        } catch (err) {
          // Error already shown by saveFilterSortConfig
          return;
        }
      } else {
        // Playground: build validated payload without saving
        const filterPayload = buildFilterPayload(
          this.playgroundFilters.groups || [],
          this.playgroundFilterGroupingConnector,
          this.columnTypeMap
        );
        this.appliedFilters = JSON.parse(JSON.stringify(filterPayload));
      }
      
      this.$store.commit('transactionMatching/SET_LAST_ACTIVE_FILTER_TAB', {
        collection: this.consolidatedCollection,
        tab: this.effectiveFilterTab
      });
      
      this.modalFilterSnapshot = {
        playgroundFilters: JSON.parse(JSON.stringify(this.playgroundFilters)),
        playgroundFilterGroupingConnector: this.playgroundFilterGroupingConnector,
        savedFilters: JSON.parse(JSON.stringify(this.savedFilters)),
      };
      this.closeFilterModal(false);
      this.page = 1;
      await this.loadData();
    },

    /**
     * Build filter payload for API request (V3 Annexure API standard)
     * @returns {Object} Filter payload matching V3 API structure
     */
    buildFilterPayload() {
      return buildFilterPayload(this.getCurrentFilterGroups(), this.getCurrentGroupingConnector(), this.columnTypeMap);
    },

    /**
     * Clear all applied filters and reload data
     */
    clearAllFilters() {
      // Reset all filter states
      this.savedFilters = this.createEmptyFilterGroup();
      this.playgroundFilters = this.createEmptyFilterGroup();
      this.playgroundFilterGroupingConnector = 'OR';
      this.appliedFilters = { groups: [], group_connectors: [] };
      this.$store.commit('transactionMatching/SET_LAST_ACTIVE_FILTER_TAB', {
        collection: this.consolidatedCollection,
        tab: null
      });
      this.page = 1;
      this.loadData();
    },

    /**
     * Get current filters and sort state for Excel download.
     * Filter source follows the last applied filter tab (Vuex); sort uses playground → applied → saved.
     * @returns {{ collection: string, filters: Object|null, sort: Array|null }}
     */
    getFiltersAndSort() {
      const { filters, sort } = resolveExportDataFilters({
        lastAppliedFilterTab: this.lastAppliedFilterTab,
        playgroundFilters: this.playgroundFilters,
        playgroundFilterGroupingConnector: this.playgroundFilterGroupingConnector,
        appliedFilters: this.appliedFilters,
        savedFilters: this.savedFilters,
        playgroundSort: this.playgroundSort,
        appliedSort: this.appliedSort,
        savedSort: this.savedSort,
        columnTypeMap: this.columnTypeMap,
        enableSavedFilterSort: this.enableSavedFilterSort,
      });

      return {
        collection: this.consolidatedCollection,
        filters,
        sort,
      };
    },

    // ========================================================================
    // Sort Modal Methods
    // ========================================================================

    /**
     * Open the sort modal
     */
    openSortModal() {
      // Initialize playground sort from applied sort if empty
      if (this.playgroundSort.length === 0 && this.appliedSort.length > 0) {
        this.playgroundSort = JSON.parse(JSON.stringify(this.appliedSort));
      }
      
      // Default to saved tab (or playground when saved tabs disabled)
      this.activeSortTab = this.enableSavedFilterSort ? 'saved' : 'playground';
      this.showSortModal = true;
    },

    /**
     * Close the sort modal
     */
    closeSortModal() {
      this.showSortModal = false;
      // Don't reset playground - keep state across modal open/close
    },

    /**
     * Get current sort items based on active tab
     * @returns {Array} Current sort items array (always returns array)
     */
    getCurrentSortItems() {
      const items = this.effectiveSortTab === 'saved' ? this.savedSort : this.playgroundSort;
      return Array.isArray(items) ? items : [];
    },

    /**
     * Toggle a column in/out of sort selection
     * @param {string} columnKey - Column key to toggle
     */
    toggleSortColumn(columnKey) {
      const currentSort = [...this.getCurrentSortItems()]; // Create a copy for reactivity
      const index = currentSort.findIndex(item => item.key === columnKey);
      
      if (index >= 0) {
        // Remove from sort
        currentSort.splice(index, 1);
      } else {
        // Add to sort with default ascending direction
        currentSort.push({
          key: columnKey,
          direction: 'asc'
        });
      }

      // Reassign to trigger Vue reactivity
      if (this.effectiveSortTab === 'saved') {
        this.savedSort = currentSort;
      } else {
        this.playgroundSort = currentSort;
      }
    },

    /**
     * Set the sort direction for a column
     * @param {string} columnKey - Column key
     * @param {string} direction - Direction ('asc' or 'desc')
     */
    setSortDirection(columnKey, direction) {
      const currentSort = this.getCurrentSortItems().map(item => {
        if (item.key === columnKey) {
          return { ...item, direction };
        }
        return item;
      });

      // Reassign to trigger Vue reactivity
      if (this.effectiveSortTab === 'saved') {
        this.savedSort = currentSort;
      } else {
        this.playgroundSort = currentSort;
      }
    },

    /**
     * Reset sort items to empty for current tab
     */
    resetSort() {
      if (this.effectiveSortTab === 'saved') {
        this.savedSort = [];
      } else {
        this.playgroundSort = [];
      }
    },

    /**
     * Apply sort configuration based on active tab
     */
    async applySort() {
      if (this.effectiveSortTab === 'saved') {
        // Save to API first, then apply
        try {
          await this.saveFilterSortConfig({ sort: this.savedSort });
          this.appliedSort = JSON.parse(JSON.stringify(this.savedSort));
        } catch (err) {
          // Error already shown by saveFilterSortConfig
          return;
        }
      } else {
        // Playground: just apply without saving
        this.appliedSort = JSON.parse(JSON.stringify(this.playgroundSort));
      }
      
      this.closeSortModal();
      
      // Reset to first page and reload
      this.page = 1;
      await this.loadData();
    },

    /**
     * Clear all sort items and reload data
     */
    clearAllSort() {
      this.savedSort = [];
      this.playgroundSort = [];
      this.appliedSort = [];
      this.page = 1;
      this.loadData();
    },

    /**
     * Download template with Unmatched records
     * Temporarily applies template filters, computes the download payload, and emits it
     */
    downloadTemplate() {
      console.log('[TMResultsDataTable]: Downloading template for unmatched/manual records');
      
      // Store current filters
      const originalFilters = JSON.parse(JSON.stringify(this.appliedFilters));
      
      // Temporarily apply filter: Match Nature contains 'Manual' OR Consol Status contains 'Unmatched'
      this.appliedFilters = TEMPLATE_FILTER_CONFIG;
      
      // Compute the download payload while template filters are applied
      const filtersAndSort = this.getFiltersAndSort();
      
      // Emit event with computed payload so parent doesn't need ref access
      this.$emit('download-excel', filtersAndSort);
      
      // Restore original filters
      this.appliedFilters = originalFilters;
    },

    /**
     * Handle Generate Excel button click (V2).
     * Emits 'generate-result' so the parent chain can trigger the backend API.
     */
    handleGenerateExcel() {
      console.log('[TMResultsDataTable]: Generate Excel triggered (V2)');
      const filtersAndSort = this.getFiltersAndSort();
      this.$emit('generate-result', {
        tables: this.tabName ? [this.tabName] : [],
        collection: filtersAndSort.collection || null,
        filters: filtersAndSort.filters || null,
        sort: filtersAndSort.sort || null,
      });
    },

    /**
     * Open bulk manual matching dialog
     */
    openBulkManualMatchingDialog() {
      console.log('[TMResultsDataTable]: Opening bulk manual matching dialog');
      this.showBulkMatchDialog = true;
      this.bulkMatchFiles = [];
      this.bulkMatchResetFlag = !this.bulkMatchResetFlag;
    },

    /**
     * Handle file upload for bulk manual matching
     * @param {Array} files - Array of uploaded files
     */
    handleBulkMatchFilesUpload(files) {
      try {
        // Validate files using utility function
        if (Array.isArray(files) && files.length > 0) {
          const validation = validateBulkMatchFiles(files);
          
          if (!validation.isValid) {
            this.bulkMatchFiles = [];
            this.$store.commit("sbError", validation.error);
            return;
          }
          
          this.bulkMatchFiles = files;
        }
      } catch (error) {
        this.bulkMatchFiles = [];
        this.$store.commit("sbError", error.message || ERROR_MESSAGES.UNEXPECTED_ERROR);
        console.error("[TMResultsDataTable]: File upload error", error);
      }
    },

    /**
     * Submit bulk manual matching files
     * Uploads files and calls API to process bulk matching
     */
    async submitBulkManualMatching() {
      console.log('[TMResultsDataTable]: Submitting bulk manual matching');
      
      if (!Array.isArray(this.bulkMatchFiles) || this.bulkMatchFiles.length === 0) {
        this.$store.commit("sbError", ERROR_MESSAGES.NO_FILE_SELECTED);
        return;
      }

      // Reset any previous errors and start processing
      this.bulkMatchErrors = [];
      this.bulkMatchErrorMessage = '';
      this.bulkMatchProcessing = true;
      
      try {
        // Upload all files
        const uploadPromises = this.bulkMatchFiles.map(async (file) => {
          try {
            const response = await this.$nova.uploadFile(this.axios, file, {});
            return response;
          } catch (err) {
            console.error("[TMResultsDataTable]: File upload error", err);
            throw err;
          }
        });
        
        const uploadResults = await Promise.all(uploadPromises);
        console.log("[TMResultsDataTable]: File upload results", uploadResults);
        
        // Check if all uploads were successful
        if (uploadResults.every((response) => response.data.status === "success")) {
          const uploadData = uploadResults.flatMap((response) => 
            response.data.data.map((item) => ({
              filename: item.uploadname,
              file_id: item._id,
              file_path: item.path
            }))
          );
          
          // Direct API call for bulk manual matching (TODO: Move to Vuex action)
          const payload = {
            upload: uploadData,
            tmaid: this.analysisDetails._id,
            runid: this.runDetails._id,
            clientCode: this.$store.state.clientCode?.toString() || '',
          };

          if (this.variant === 'v2') {
            payload.version = 'v2';
            payload.collection = this.consolidatedCollection;
            const fileObjectId = this.fileConfigInfo[this.fileConfigId]?.file;
            if (fileObjectId) {
              payload.fileObjectId = fileObjectId;
            }
          }
          
          const apiResponse = await this.axios.post(
            '/v3/bot/analysis/bulk-manual-match-consolidated',
            payload
          );
          
          if (apiResponse.data.status === 'success') {
            this.$store.commit('sbSuccess', apiResponse.data.message || SUCCESS_MESSAGES.BULK_MATCH_SUBMITTED);
            this.closeBulkMatchDialog();
            // Reload data to show updated results
            // await this.loadData();
            // Emit event to parent to refresh if needed
            this.$emit('refresh-data');
          } else {
            // Handle validation failure with row-level errors
            this.handleBulkMatchValidationError(apiResponse.data);
          }
        } else {
          throw new Error(ERROR_MESSAGES.UPLOAD_FAILED);
        }
      } catch (err) {
        // Check if the error response contains validation data
        if (err.response && err.response.data) {
          this.handleBulkMatchValidationError(err.response.data);
        } else {
          this.$store.commit("sbError", err.message || err || ERROR_MESSAGES.UNKNOWN_ERROR);
          console.error("[TMResultsDataTable]: Submit bulk manual matching error", err);
        }
      } finally {
        this.bulkMatchProcessing = false;
      }
    },

    /**
     * Handle bulk match validation error response
     * Extracts and displays row-level errors or overall error message
     * @param {Object} responseData - API error response data
     */
    handleBulkMatchValidationError(responseData) {
      console.log('[TMResultsDataTable]: Handling bulk match validation error', responseData);
      
      const { errorMessage, errors } = transformBulkMatchErrors(responseData);
      this.bulkMatchErrorMessage = errorMessage;
      this.bulkMatchErrors = errors;
    },

    /**
     * Reset bulk match errors and allow user to try again
     */
    resetBulkMatchErrors() {
      this.bulkMatchErrors = [];
      this.bulkMatchErrorMessage = '';
      this.bulkMatchFiles = [];
      this.bulkMatchResetFlag = !this.bulkMatchResetFlag;
    },

    /**
     * Close bulk match dialog and reset all state
     */
    closeBulkMatchDialog() {
      this.showBulkMatchDialog = false;
      this.bulkMatchFiles = [];
      this.bulkMatchErrors = [];
      this.bulkMatchErrorMessage = '';
      this.bulkMatchProcessing = false;
      this.bulkMatchResetFlag = !this.bulkMatchResetFlag;
    }
  }
};
</script>

<style lang="scss" scoped>
@import './TMResultsDataTable.scss';
</style>

<!-- TODO: only on page referesh the data is again obtained, that needs to be corrected to get on refresh data and on navigating to the results tab also -->
